import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    AccessibilityInfo,
    type PointerEvent,
    Animated,
    Platform,
    View
} from "react-native";
import type IFieldControlProps from "./type";
import {
    type FieldInputAccessibilityType,
    type IFieldHintProps,
    type FieldActionType,
    type FieldType
} from "./type";
import stylesheet, {
    getFieldIconColor,
    getFieldColors,
    getFieldSize,
    getFieldIDs,
    useStyles
} from "./stylesheet";
import FieldAction from "./internal/action";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    getSlideStyle,
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    useFieldContext
} from "../field/context";
import {
    CircleQuestionMark as CircleQuestionMarkIcon,
    TriangleAlert as TriangleAlertIcon,
    CircleCheck as CircleCheckIcon,
    CircleAlert as CircleAlertIcon,
    Info as InfoIcon,
    type LucideIcon
} from "lucide-react-native";
import {
    getFieldControlAccessibility
} from "../field/util";
import {
    LabelBase
} from "../label";
import Loading from "../loading";
import Text from "../text";

const FieldHintTextIcon: Record<FieldType, LucideIcon> = {
    neutral: CircleQuestionMarkIcon,
    warning: TriangleAlertIcon,
    success: CircleCheckIcon,
    danger: CircleAlertIcon,
    info: InfoIcon
};

const preventNonInputFocusSteal = (event: {
    target: EventTarget | null;
    preventDefault: () => void;
}) => {
    const tagName = (event.target as Element | null)?.tagName;

    if(tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
        return;
    }

    event.preventDefault();
};

const FieldHint: FC<IFieldHintProps> = ({
    customTheme,
    isVisible,
    children,
    offset
}) => {
    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        exitIntent: "collapse",
        enterIntent: "expand",
        customTheme,
        isVisible
    });

    const contentRef = useRef(children);

    const slideStyle = useMemo(() => getSlideStyle({
        isReduceMotionEnabled,
        progress,
        offset
    }), [
        isReduceMotionEnabled,
        progress,
        offset
    ]);

    if(isVisible) {
        contentRef.current = children;
    }

    if(!isRendered) {
        return null;
    }

    return <Animated.View
        style={slideStyle}
    >
        {contentRef.current}
    </Animated.View>;
};

const FieldControl: FC<IFieldControlProps> = ({
    hintTextIcon: HintTextIconProp,
    spreadBehaviour = "baseline",
    isShowHintTextIcon = false,
    isCounterWarning = false,
    isMinWidthEnabled = true,
    hintTextContainerStyle,
    isCounterLive = false,
    isCounterOver = false,
    isMultiline = false,
    accessibilityLabel,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    customBorderColor,
    isFocused = false,
    isLoading = false,
    customTitleColor,
    type = "neutral",
    size = "medium",
    customLocalize,
    onFocusRequest,
    controlStyle,
    counterText,
    customColor,
    customTheme,
    renderInput,
    startAction,
    endAction,
    maxHeight,
    minHeight,
    hintText,
    subTitle,
    inputID,
    style,
    title,
    ...props
}) => {
    const {
        surfaceColor: parentSurfaceColor,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme, customLocalize);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const fieldContext = useFieldContext();

    const pointerType = usePointerType();

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const currentSize = getFieldSize({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces,
        size
    });

    const currentColors = getFieldColors({
        surfaceColor: parentSurfaceColor,
        customBorderColor,
        customTitleColor,
        isCounterWarning,
        isCounterOver,
        customColor,
        isDisabled,
        isReadOnly,
        colors,
        type
    });

    const fieldIDs = getFieldIDs({
        inputID
    });

    const {
        hintTextIcon: hintTextIconDynamicStyle,
        container: containerDynamicStyle,
        hintText: hintTextDynamicStyle,
        control: controlDynamicStyle,
        input: inputDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        isMinWidthEnabled,
        userStyle: style,
        spreadBehaviour,
        currentColors,
        shapeConfigs,
        pointerType,
        currentSize,
        isMultiline,
        shadowAlpha,
        typography,
        elevations,
        isDisabled,
        isReadOnly,
        isFocused,
        isHovered,
        maxHeight,
        minHeight,
        radiuses,
        borders,
        colors,
        spaces,
        type
    });

    useEffect(() => {
        if(Platform.OS === "web" || type !== "danger" || !hintText) {
            return;
        }

        AccessibilityInfo.announceForAccessibility(hintText);
    }, [
        hintText,
        type
    ]);

    useEffect(() => {
        if(Platform.OS !== "ios" || !isFocused || !isCounterLive || !counterText) {
            return;
        }

        AccessibilityInfo.announceForAccessibility(counterText);
    }, [
        counterText
    ]);

    const webFocusProps = Platform.OS === "web" ? {
        onMouseDown: preventNonInputFocusSteal
    } : {};

    const getIsFocusResponder = () => {
        return !isDisabled && !!onFocusRequest;
    };

    const onFocusResponderRelease = () => {
        if(onFocusRequest) {
            onFocusRequest();
        }
    };

    const onControlPointerEnter = (event: PointerEvent) => {
        if(event.nativeEvent.pointerType === "touch") {
            return;
        }

        setIsHovered(true);
    };

    const onControlPointerLeave = () => {
        setIsHovered(false);
    };

    const getBaseInputAccessibilityProps = (): FieldInputAccessibilityType => {
        const accessibilityProps: FieldInputAccessibilityType = {
            id: inputID
        };

        if(isDisabled) {
            accessibilityProps["aria-disabled"] = true;
        }

        if(isLoading) {
            accessibilityProps["aria-busy"] = true;
        }

        if(Platform.OS === "web") {
            if(title) {
                accessibilityProps["aria-labelledby"] = fieldIDs.labelID;
            } else if(accessibilityLabel) {
                accessibilityProps["aria-label"] = accessibilityLabel;
            }

            if(hintText || counterText) {
                accessibilityProps["aria-describedby"] = fieldIDs.hintTextID;
            }

            if(type === "danger") {
                accessibilityProps["aria-invalid"] = true;
            }

            if(isRequired) {
                accessibilityProps["aria-required"] = true;
            }

            if(isDisabled) {
                accessibilityProps.disabled = true;
            }

            return accessibilityProps;
        }

        const hintParts = [
            isRequired ? localize("field-required") : undefined,
            type === "danger" ? localize("field-invalid") : undefined,
            hintText
        ].filter(Boolean);

        if(hintParts.length) {
            accessibilityProps.accessibilityHint = hintParts.join(", ");
        }

        if(!title) {
            if(accessibilityLabel) {
                accessibilityProps.accessibilityLabel = accessibilityLabel;
            }

            return accessibilityProps;
        }

        if(Platform.OS === "ios") {
            accessibilityProps.accessibilityLabel = subTitle ? `${title}, ${subTitle}` : title;
        } else {
            accessibilityProps["aria-labelledby"] = fieldIDs.labelID;
        }

        return accessibilityProps;
    };

    const getInputAccessibilityProps = (): FieldInputAccessibilityType => {
        const accessibilityProps = getBaseInputAccessibilityProps();

        const fieldAccessibility = getFieldControlAccessibility({
            context: fieldContext,
            isLabelled: !!title
        });

        if(fieldAccessibility.labelledBy) {
            if(Platform.OS === "ios") {
                accessibilityProps.accessibilityLabel = fieldAccessibility.accessibilityLabel;
            } else {
                accessibilityProps["aria-labelledby"] = fieldAccessibility.labelledBy;
            }
        }

        if(fieldAccessibility.describedBy) {
            const describedByIDs = [
                ...(accessibilityProps["aria-describedby"]?.split(" ") ?? []),
                ...fieldAccessibility.describedBy.split(" ")
            ];

            accessibilityProps["aria-describedby"] = [...new Set(describedByIDs)].join(" ");
        }

        if(fieldAccessibility.accessibilityHint) {
            accessibilityProps.accessibilityHint = [
                accessibilityProps.accessibilityHint,
                fieldAccessibility.accessibilityHint
            ].filter(Boolean).join(", ");
        }

        if(fieldAccessibility.isInvalid) {
            accessibilityProps["aria-invalid"] = true;
        }

        return accessibilityProps;
    };

    const renderLabel = () => {
        if(!title) {
            return null;
        }

        return <View
            onStartShouldSetResponder={getIsFocusResponder}
            onResponderRelease={onFocusResponderRelease}
            {...webFocusProps}
        >
            <LabelBase
                titleColor={customTitleColor ? currentColors.labelColor : undefined}
                nativeID={Platform.OS === "web" ? undefined : fieldIDs.labelID}
                id={Platform.OS === "web" ? fieldIDs.labelID : undefined}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isDisabled={isDisabled}
                isRequired={isRequired}
                optionalText={subTitle}
                isRow={true}
            >
                {title}
            </LabelBase>
        </View>;
    };

    const renderIcon = (action: FieldActionType) => {
        const IconComponent = action.icon;

        return <View
            style={[
                action.style,
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <IconComponent
                customColor={getFieldIconColor({
                    customIconColor: action.customIconColor,
                    currentColors,
                    isDisabled,
                    colors
                })}
                color={currentColors.iconColorKey}
                size={currentSize.iconSize}
            />
        </View>;
    };

    const renderAction = (action?: FieldActionType) => {
        if(!action) {
            return null;
        }

        if(!action.onPress) {
            return renderIcon(action);
        }

        return <FieldAction
            controlsID={action.isControllingInput ? inputID : undefined}
            accessibilityLabel={action.accessibilityLabel}
            isDisabled={isDisabled || !!action.isDisabled}
            customIconColor={action.customIconColor}
            currentColors={currentColors}
            isPressed={action.isPressed}
            isVisible={action.isVisible}
            currentSize={currentSize}
            customTheme={customTheme}
            isMultiline={isMultiline}
            onPress={action.onPress}
            style={action.style}
            icon={action.icon}
        />;
    };

    const renderEndAction = () => {
        if(!isLoading) {
            return renderAction(endAction);
        }

        return <View
            style={[
                stylesheet.icon,
                iconDynamicStyle
            ]}
        >
            <Loading
                customColor={currentColors.iconColor}
                size={currentSize.iconSize}
                customTheme={customTheme}
            />
        </View>;
    };

    const renderHintTextIcon = () => {
        if(!hintText || type === "neutral" && !isShowHintTextIcon) {
            return null;
        }

        if(HintTextIconProp) {
            return <View
                style={[
                    stylesheet.hintTextIcon,
                    hintTextIconDynamicStyle
                ]}
                aria-hidden={true}
            >
                <HintTextIconProp
                    customColor={currentColors.hintTextIconColor}
                    color={currentColors.hintTextIconColorKey}
                    size={iconSizes.small}
                />
            </View>;
        }

        const CurrentHintTextIcon = FieldHintTextIcon[type];

        return <View
            style={[
                stylesheet.hintTextIcon,
                hintTextIconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <CurrentHintTextIcon
                color={currentColors.hintTextIconColor}
                size={iconSizes.small}
            />
        </View>;
    };

    const renderHintText = () => {
        if(!hintText) {
            return null;
        }

        return <Text
            role={type === "danger" && Platform.OS === "web" ? "alert" : undefined}
            customColor={currentColors.hintTextColor}
            style={stylesheet.hintTextContent}
            customTheme={customTheme}
            variant="bodyMediumSize"
        >
            {hintText}
        </Text>;
    };

    const renderCounter = () => {
        if(!counterText) {
            return null;
        }

        return <Text
            aria-live={isCounterLive ? "polite" : "off"}
            customColor={currentColors.counterColor}
            style={stylesheet.counter}
            customTheme={customTheme}
            variant="bodySmallSize"
        >
            {counterText}
        </Text>;
    };

    const renderHint = () => {
        const isHintVisible = !!hintText || !!counterText;

        return <FieldHint
            offset={-spaces.spacingXs}
            customTheme={customTheme}
            isVisible={isHintVisible}
        >
            {
                isHintVisible ?
                    <View
                        style={[
                            hintTextContainerStyle,
                            stylesheet.hintText,
                            hintTextDynamicStyle
                        ]}
                        id={fieldIDs.hintTextID}
                    >
                        {renderHintTextIcon()}
                        {renderHintText()}
                        {renderCounter()}
                    </View>
                :
                    null
            }
        </FieldHint>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderLabel()}
        <View
            onStartShouldSetResponder={getIsFocusResponder}
            onResponderRelease={onFocusResponderRelease}
            onPointerEnter={onControlPointerEnter}
            {...webFocusProps}
            onPointerLeave={onControlPointerLeave}
            style={[
                controlStyle,
                stylesheet.control,
                controlDynamicStyle
            ]}
        >
            {renderAction(startAction)}
            {
                renderInput({
                    placeholderTextColor: currentColors.placeholderColor,
                    accessibilityProps: getInputAccessibilityProps(),
                    style: inputDynamicStyle
                })
            }
            {renderEndAction()}
        </View>
        {renderHint()}
    </View>;
};
export {
    FieldAction
};
export default FieldControl;
